<script lang="ts" context="module">
	/**
	 * Ids for the controls that are given none.
	 *
	 * Several of these are filters in a control panel with no label of their own, and two of them
	 * routinely sit side by side (the delivery tables filter by application *and* by status). The
	 * listbox and every option carry an id derived from the field's, so with no id at all the two
	 * controls on one screen would emit the same `#-listbox` twice — which `aria-controls` then
	 * resolves to whichever the browser saw first.
	 */
	let sequence = 0;
</script>

<script lang="ts">
	/**
	 * The one dropdown in the app, everywhere a value is chosen from a fixed list.
	 *
	 * It replaced flowbite's `Select`, which is a native `<select>`. A native select is not a bad
	 * control, it is an *unstyleable* one: the closed field takes the app's border, background and
	 * focus ring while the open list is drawn by the operating system, so the one moment the
	 * customer is actually reading options is the one moment the store stops looking like itself —
	 * white rows in the platform's own font over a dark page, no depth surface, no rounded corners,
	 * and on Windows a list that ignores the dark theme entirely. It also cannot hold a second line
	 * per row, cannot be filtered, and cannot say why an entry is there.
	 *
	 * The shape is `CountrySelect`'s, and deliberately so — that control had already worked out the
	 * hard parts (a listbox that is the authority on what may be picked, options committed on
	 * `mousedown` so the close-on-blur ordering cannot swallow them, Escape stopped so it closes the
	 * list rather than the modal around it) and a store with two different dropdowns is a store where
	 * the keyboard means two different things depending on which field you are in. The one
	 * difference is the trigger: `CountrySelect` is a text box because 249 countries can only be
	 * found by typing, while this opens from a **button** that reads the current selection, because
	 * "Test / Live" is a choice rather than a search. Long lists get the search box back, inside the
	 * popup, above the options — see `hasSearch`.
	 *
	 * **What is typed never becomes the value**, exactly as in `CountrySelect`: the search box
	 * narrows the list and only an option can be chosen from it. That is what keeps a hand-typed
	 * `VAT` out of a field whose wire values are `eu_vat` and `gb_vat`.
	 */

	import { createEventDispatcher, tick } from 'svelte';
	import { anchoredPopup } from './anchoredPopup';
	import { searchKey } from '../Countries';
	import type { SelectOption } from '../AppTypes';

	/** The rows on offer, in the order they should be read. */
	export let items: SelectOption[] = [];
	/** The chosen row's `value`. `''` is "nothing chosen". Bindable. */
	export let value: string = '';
	/** `for`/`id` wiring — pass the same value as the enclosing `FormField`'s `id`. */
	export let id: string = '';
	export let disabled: boolean = false;
	/**
	 * Announced to assistive technology, **not** enforced by the browser — the rule
	 * `CountrySelect` documents. Every form here gates its own submit in JavaScript, so a native
	 * `required` would only add a browser bubble about a control the browser thinks is a button.
	 */
	export let required: boolean = false;
	/** What the closed control reads while nothing is chosen. */
	export let placeholder: string = 'Choose an option…';
	export let size: 'sm' | 'md' = 'md';
	/**
	 * Whether the popup carries a search box.
	 *
	 * Left unset it is decided by length: a list you can take in at a glance is one where a search
	 * box is a keystroke between the customer and an answer they can already see, and a list past
	 * about eight rows is one where scrolling is the slow part. Set it explicitly where the caller
	 * knows better than the count does.
	 */
	export let searchable: boolean | undefined = undefined;
	export let searchPlaceholder: string = 'Type to filter…';

	let cssClass: string = '';
	export { cssClass as class };

	/** Past this many rows the popup grows a search box. See `searchable`. */
	const SEARCH_THRESHOLD = 8;

	const dispatcher = createEventDispatcher<{ change: { value: string } }>();

	const fallbackID = `select-field-${(sequence += 1)}`;

	let open: boolean = false;
	let query: string = '';
	let activeIndex: number = 0;
	let wrapper: HTMLDivElement | undefined;
	let trigger: HTMLButtonElement | undefined;
	let search: HTMLInputElement | undefined;
	let list: HTMLUListElement | undefined;
	/**
	 * The popup, which lives on `document.body` rather than inside the wrapper (see
	 * `anchoredPopup`). It is bound because the focus guard below has to count it as part of the
	 * control — without that, focus moving to the search box reads as focus leaving the field.
	 */
	let popup: HTMLDivElement | undefined;

	/**
	 * The letters typed at a control with no search box, and when they were typed.
	 *
	 * A native select jumps to the row you start spelling, and losing that is the one thing a
	 * hand-rolled dropdown is routinely worse at. The buffer clears after a second so "in" finds
	 * India and a later "d" starts again at Denmark rather than looking for "ind".
	 */
	let typed: string = '';
	let typedAt: number = 0;

	$: fieldID = id === '' ? fallbackID : id;
	$: hasSearch = searchable ?? items.length > SEARCH_THRESHOLD;

	/**
	 * The rows the query matches, prefix matches first.
	 *
	 * The same rule `filterCountries` uses and for the same reason: a match at the start of a name
	 * is what somebody typing usually means, so "fix" offers "Fixed amount off" ahead of "A
	 * percentage applied before a fixed one". Diacritics are stripped on both sides, because
	 * catalogue names are whatever an admin typed and the reader's keyboard is whatever they have.
	 */
	$: rows = fnFilter(items, hasSearch ? query : '');

	/**
	 * What the closed control reads.
	 *
	 * Resolved against `items` rather than remembered, so a value the list no longer carries — a
	 * status filter held across a reload, a family that has since been archived — renders as the
	 * value itself instead of as an empty box that looks unset.
	 */
	$: selected = items.find(item => item.value === value);
	$: selectionLabel = selected?.name ?? (value === '' ? '' : value);

	$: triggerClass =
		size === 'sm'
			? 'block w-full rounded-lg border border-gray-300 bg-gray-50 p-2 pr-9 text-left text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white'
			: 'block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 pr-9 text-left text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white';

	function fnFilter(all: SelectOption[], text: string): SelectOption[] {
		const needle = searchKey(text);
		if (needle === '') return all;

		const matched: { item: SelectOption; isPrefix: boolean }[] = [];
		for (const item of all) {
			const name = searchKey(item.name);
			const key = item.value.toLowerCase();
			if (name.startsWith(needle) || key.startsWith(needle)) {
				matched.push({ item, isPrefix: true });
			} else if (name.includes(needle) || searchKey(item.description ?? '').includes(needle)) {
				matched.push({ item, isPrefix: false });
			}
		}

		// A stable partition rather than a sort, so the caller's own order survives inside each half.
		return [
			...matched.filter(entry => entry.isPrefix).map(entry => entry.item),
			...matched.filter(entry => !entry.isPrefix).map(entry => entry.item)
		];
	}

	/**
	 * The heading each row is drawn under, or `''` for none.
	 *
	 * Computed per row rather than by nesting the options inside their groups, so `activeIndex`
	 * stays a plain index into `rows` — a nested render makes the arrow keys walk two structures at
	 * once, which is how a heading ends up selectable.
	 */
	$: headings = rows.map((row, index) => {
		const group = row.group ?? '';
		if (group === '') return '';
		return group === (rows[index - 1]?.group ?? '') ? '' : group;
	});

	async function fnOpen(): Promise<void> {
		if (disabled || open) return;
		open = true;
		query = '';
		activeIndex = Math.max(
			0,
			rows.findIndex(row => row.value === value)
		);

		await tick();
		// The search box takes focus so typing narrows the list; with no search box focus stays on
		// the trigger, which is what the arrow keys and the typeahead are bound to.
		search?.focus();
		void fnScrollActiveIntoView();
	}

	function fnClose(returnFocus: boolean = true): void {
		if (!open) return;
		open = false;
		query = '';
		if (returnFocus) trigger?.focus();
	}

	function fnChoose(item: SelectOption): void {
		if (item.disabled === true) return;
		value = item.value;
		dispatcher('change', { value: item.value });
		fnClose();
	}

	async function fnScrollActiveIntoView(): Promise<void> {
		await tick();
		// `block: 'nearest'`, so the list only moves when the active row is genuinely off-screen.
		list?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
	}

	function fnMove(step: number): void {
		if (rows.length === 0) return;
		activeIndex = (activeIndex + step + rows.length) % rows.length;
		void fnScrollActiveIntoView();
	}

	/** The typeahead a native select gives for free. Only where there is no search box to type into. */
	function fnTypeahead(key: string): boolean {
		if (hasSearch || key.length !== 1 || key === ' ') return false;

		const now = Date.now();
		typed = now - typedAt > 1000 ? key : typed + key;
		typedAt = now;

		const needle = searchKey(typed);
		const index = rows.findIndex(row => searchKey(row.name).startsWith(needle));
		if (index < 0) return false;

		activeIndex = index;
		if (open) {
			void fnScrollActiveIntoView();
		} else {
			// Closed, the match is the choice: this is the one path where a keystroke commits, and
			// it is the behaviour every native select on every platform already has.
			fnChoose(rows[index]);
		}
		return true;
	}

	function fnKeyDown(event: KeyboardEvent): void {
		if (disabled) return;

		if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			event.preventDefault();
			if (!open) {
				void fnOpen();
				return;
			}
			fnMove(event.key === 'ArrowDown' ? 1 : -1);
			return;
		}

		if (event.key === 'Home' || event.key === 'End') {
			if (!open) return;
			event.preventDefault();
			activeIndex = event.key === 'Home' ? 0 : Math.max(0, rows.length - 1);
			void fnScrollActiveIntoView();
			return;
		}

		if (event.key === 'Enter' || (event.key === ' ' && !hasSearch)) {
			if (!open) {
				// Enter on a closed control inside a form would otherwise submit it, which from the
				// customer's side is a form sent by an attempt to open a dropdown.
				event.preventDefault();
				void fnOpen();
				return;
			}
			event.preventDefault();
			const row = rows[activeIndex];
			if (row !== undefined) fnChoose(row);
			return;
		}

		if (event.key === 'Escape') {
			if (!open) return;
			// Stopped as well as defaulted: these sit inside modals, and an Escape that bubbles
			// closes the whole dialog when the user meant to close the list.
			event.preventDefault();
			event.stopPropagation();
			fnClose();
			return;
		}

		if (event.key === 'Tab') {
			// Not `preventDefault`ed: Tab out of a dropdown belongs to the form, not to the popup.
			fnClose(false);
			return;
		}

		if (fnTypeahead(event.key)) {
			event.preventDefault();
		}
	}

	function fnInput(event: Event): void {
		query = (event.target as HTMLInputElement).value;
		// The list is a different list now, so an index into the old one means nothing.
		activeIndex = 0;
	}

	/**
	 * Closes when focus leaves the control altogether.
	 *
	 * `focusout` rather than `blur` on each part, because focus legitimately moves *within* the
	 * control — the trigger hands it to the search box on open and takes it back on a choice — and
	 * a per-element blur would close the popup in the middle of opening it.
	 *
	 * Bound to the **trigger's wrapper and to the popup**, which are two elements rather than one
	 * because the popup is portaled to `document.body`. An event in it does not bubble to the
	 * wrapper any more, so the wrapper alone sees focus arrive at the search box and never sees it
	 * leave — a list that opens, filters, and then will not close on a click elsewhere.
	 */
	function fnFocusOut(event: FocusEvent): void {
		const next = event.relatedTarget;
		if (next instanceof Node && fnHolds(next)) return;
		fnClose(false);
	}

	/**
	 * Whether a node is part of this control — trigger or popup.
	 *
	 * Two containers rather than one because the popup is portaled out to `document.body` to escape
	 * the scroll box of whatever it was opened inside. `wrapper.contains` alone was the whole test
	 * while the list was a child; on its own now it closes the list the instant the search box
	 * takes focus, which reads as a dropdown that will not stay open.
	 */
	function fnHolds(node: Node): boolean {
		return wrapper?.contains(node) === true || popup?.contains(node) === true;
	}
</script>

<div class="relative {cssClass}" bind:this={wrapper} on:focusout={fnFocusOut}>
	<div class="relative">
		<button
			{...$$restProps}
			bind:this={trigger}
			{disabled}
			id={fieldID}
			type="button"
			role="combobox"
			aria-haspopup="listbox"
			aria-required={required ? 'true' : undefined}
			aria-expanded={open}
			aria-controls={`${fieldID}-listbox`}
			aria-activedescendant={open && !hasSearch && rows[activeIndex] !== undefined
				? `${fieldID}-option-${rows[activeIndex].value || 'none'}`
				: undefined}
			class={triggerClass}
			on:click={() => (open ? fnClose() : void fnOpen())}
			on:keydown={fnKeyDown}
		>
			{#if selectionLabel === ''}
				<span class="text-gray-500 dark:text-zinc-400">{placeholder}</span>
			{:else}
				<span class="block truncate">{selectionLabel}</span>
			{/if}
		</button>
		<!--
			Decoration only, and `aria-hidden` for that reason: the button above is the combobox, and
			a second focusable thing here would put a tab stop between the field and its list.
		-->
		<i
			class="fa-solid fa-chevron-{open ? 'up' : 'down'} pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs opacity-60"
			aria-hidden="true"
		/>
	</div>

	{#if open}
		<!--
			Positioned by `anchoredPopup`, which moves it to `document.body`: inside a modal an
			absolutely-positioned list counts toward the dialog's scroll height, so opening a picker
			near the foot of a form makes the whole modal scroll under the reader's hand.
		-->
		<!-- svelte-ignore a11y-no-static-element-interactions -->
		<div
			bind:this={popup}
			use:anchoredPopup={{ anchor: wrapper }}
			on:focusout={fnFocusOut}
			class="widget bg-depth p-1 shadow-widget border-widget"
		>
			{#if hasSearch}
				<input
					bind:this={search}
					type="text"
					autocomplete="off"
					aria-label={searchPlaceholder}
					aria-controls={`${fieldID}-listbox`}
					aria-activedescendant={rows[activeIndex] !== undefined
						? `${fieldID}-option-${rows[activeIndex].value || 'none'}`
						: undefined}
					class="mb-1 block w-full rounded-lg border border-gray-300 bg-gray-50 p-2 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white dark:placeholder-zinc-400"
					placeholder={searchPlaceholder}
					value={query}
					on:input={fnInput}
					on:keydown={fnKeyDown}
				/>
			{/if}

			<ul bind:this={list} id={`${fieldID}-listbox`} role="listbox" class="max-h-64 overflow-y-auto">
				{#if rows.length === 0}
					<!-- Said rather than shown as an empty box: a picker with nothing in it and no words
					     reads as broken, when the true answer is that the query matched nothing. -->
					<li class="px-3 py-2 text-sm text-default">
						{query === '' ? 'Nothing to choose from.' : `Nothing matches “${query}”.`}
					</li>
				{:else}
					{#each rows as row, index (row.value)}
						{#if headings[index] !== ''}
							<li
								role="presentation"
								class="card-label px-3 pb-1 pt-2 first:pt-1"
							>
								{headings[index]}
							</li>
						{/if}
						<li
							id={`${fieldID}-option-${row.value || 'none'}`}
							role="option"
							aria-selected={row.value === value}
							aria-disabled={row.disabled === true ? 'true' : undefined}
							data-active={index === activeIndex}
							class="rounded-lg px-3 py-1.5 text-sm {row.disabled === true
								? 'cursor-not-allowed opacity-50'
								: 'cursor-pointer'} {index === activeIndex ? 'bg-primary-100 dark:bg-primary-900/40' : ''}"
							on:mouseenter={() => (activeIndex = index)}
							on:mousedown|preventDefault={() => fnChoose(row)}
						>
							<span class="flex items-center gap-2">
								<span class="grow">{row.name}</span>
								{#if row.value === value}
									<i class="fa-solid fa-check text-xs text-primary-600 dark:text-primary-400" aria-hidden="true" />
								{/if}
							</span>
							{#if (row.description ?? '') !== ''}
								<span class="block text-xs text-default">{row.description}</span>
							{/if}
						</li>
					{/each}
				{/if}
			</ul>
		</div>
	{/if}
</div>
